import { useCallback } from 'react';

import { jump_to_specific_scene_set } from '@bighealth/api/SceneSetGraph/v1';

import { useContentRedirectContext } from 'components/ProvidersContainer/ContentRedirectProvider';
import { getQueryClient } from 'components/ProvidersContainer/getQueryClient';
import { PreviousHistoryState } from 'components/Routes/types';
import { SceneSetParams, useSafeParams } from 'components/Routes/useSafeParams';
import { isMobileWeb } from 'config/isMobileWeb';
import { useHistory } from 'cross-platform/react-router';
import {
  useLazyQueryTransitionSceneSetWithAssets,
  useQueryProduct,
  useQueryProgram,
} from 'lib/api/reactQueryHelpers';
import getPathForParams from 'lib/player/getPathForParams';
import { getIsSceneAtEndOfSceneSet } from 'lib/player/sceneSetHelpers';
import { getNextPath } from 'lib/player/sceneSetHelpers/getNextPath';

import { calcDidSceneSetChange } from './useActionHandler/helpers/calcDidSceneSetChange';

export type UseNavigateToNextContent = () => void;

/**
 * A hook that roles up some of the calculations necessary to determine which
 * Scene (potentially within the current or a new SceneSet) the browser
 * should navigate to.
 *
 */
export const useNavigateToNextContent = (): UseNavigateToNextContent => {
  const {
    productReference,
    sceneSetGraphId,
    sceneSetId,
    sceneId,
  } = useSafeParams<SceneSetParams>();
  const history = useHistory<PreviousHistoryState>();
  const programId = useQueryProgram()?.data?.result.id;
  const productId = useQueryProduct()?.data?.result.id;
  const queryClient = getQueryClient();

  const currentPath = history.location.pathname;
  const transitionSceneSetWithAssets = useLazyQueryTransitionSceneSetWithAssets();
  const currentSceneSet = queryClient.getQueryData<
    jump_to_specific_scene_set.Response
  >(['SceneSet', sceneSetId])?.result?.scene_set_json;
  const isSceneAtEndOfSceneSet = getIsSceneAtEndOfSceneSet(
    currentSceneSet,
    sceneId
  );
  const { exitContent } = useContentRedirectContext();

  return useCallback(async () => {
    console.log(
      '[SLEEPIO-5842] useNavigateToNextContent called:',
      JSON.stringify({
        isSceneAtEndOfSceneSet,
        isMobileWeb,
        sceneSetGraphId,
        sceneSetId,
        sceneId,
      })
    );
    if (isSceneAtEndOfSceneSet) {
      const nextSceneSetResult = await transitionSceneSetWithAssets({
        current_graph_id: sceneSetGraphId,
        current_product_id: productId as number,
        current_program_id: programId as number,
        current_scene_set_id: sceneSetId,
        enable_inter_graph_transitions: true,
      });
      const nextSceneSet = nextSceneSetResult?.result;
      const didSceneSetChange = calcDidSceneSetChange(
        currentPath,
        history.location.pathname
      );
      console.log(
        '[SLEEPIO-5842] useNavigateToNextContent transition result:',
        JSON.stringify({
          nextSceneSetId: nextSceneSet?.id,
          nextSceneSetGraphId: nextSceneSet?.scene_set_graph_id,
          didSceneSetChange,
          currentPath,
          historyPathAfterAwait: history.location.pathname,
        })
      );
      if (didSceneSetChange) {
        // Do not fetch the next SceneSet if the current path (history.location.pathname)
        // is not equal to the previous SceneSet (currentPath).
        console.log(
          '[SLEEPIO-5842] useNavigateToNextContent bailing early: didSceneSetChange=true'
        );
        return;
      }
      if (nextSceneSet) {
        const destinationGraphId =
          nextSceneSet.scene_set_graph_id ?? sceneSetGraphId;
        // On mobile web, cross-graph navigation leads to a blocked session
        // (MobileWebGate "Uh Oh"). Redirect to the Sessions page instead so
        // the backend can serve the correct post-session mobile-web state.
        if (isMobileWeb && destinationGraphId !== sceneSetGraphId) {
          console.log(
            '[SLEEPIO-5842] useNavigateToNextContent GUARD FIRED: redirecting to home instead of crossing graph',
            JSON.stringify({ sceneSetGraphId, destinationGraphId })
          );
          exitContent({ fallback: 'home', productReference });
          return;
        }
        console.log(
          '[SLEEPIO-5842] useNavigateToNextContent GUARD DID NOT FIRE: navigating to',
          JSON.stringify({
            destinationGraphId,
            nextSceneSetId: nextSceneSet.id,
          })
        );
        const path = getPathForParams({
          productReference,
          sceneSetGraphId: destinationGraphId,
          sceneSetId: nextSceneSet.id,
          sceneId: 0,
        });
        history.push(path, {
          pathname: history.location.pathname,
        });
      } else {
        exitContent({ fallback: 'home', productReference });
        return;
      }
    } else {
      // Just move to the next Scene
      if (currentSceneSet) {
        const nextPath = getNextPath(currentPath);
        history.push(nextPath, {
          pathname: history.location.pathname,
        });
      }
    }
  }, [
    currentPath,
    currentSceneSet,
    exitContent,
    history,
    isSceneAtEndOfSceneSet,
    productId,
    productReference,
    programId,
    sceneId,
    sceneSetGraphId,
    sceneSetId,
    transitionSceneSetWithAssets,
  ]);
};
